import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import axios from 'axios';

const API_BASE = process.env.REACT_APP_BACKEND_API || '';

/** Align with BK monorepo MainButton / TransparentButton */
const BK_PRIMARY_BUTTON_SX = {
  backgroundColor: '#1c32f3',
  color: '#fff',
  borderRadius: '8px',
  fontFamily: 'Satoshi, sans-serif',
  fontWeight: 500,
  fontSize: '14px',
  lineHeight: '24px',
  textTransform: 'none',
  boxShadow: 'none',
  px: 3,
  py: 1.25,
  '&:hover': {
    backgroundColor: '#0036cc',
    boxShadow: 'none',
  },
  '&:disabled': {
    backgroundColor: 'rgba(28, 50, 243, 0.45)',
    color: '#fff',
  },
};

const BK_TRANSPARENT_BUTTON_SX = {
  backgroundColor: 'transparent',
  color: '#111E92',
  borderRadius: '8px',
  fontFamily: 'Satoshi, sans-serif',
  fontWeight: 500,
  fontSize: '14px',
  lineHeight: '20px',
  textTransform: 'none',
  boxShadow: 'none',
  border: 'none',
  px: 2,
  py: 1.25,
  '&:hover': {
    backgroundColor: 'transparent',
    boxShadow: 'none',
    textDecoration: 'underline',
  },
  '&:disabled': {
    color: 'rgba(17, 30, 146, 0.45)',
  },
};

const STATUS_LABEL_HU = {
  draft: 'Piszkozat',
  pending: 'Függőben',
  accepted: 'Elfogadva',
  disputed: 'Vitatott',
  paid: 'Fizetve',
};

const CATEGORY_LABEL_HU = {
  continuous: 'Folyamatos szolgáltatás',
  one_time: 'Egyszeri szolgáltatás',
  consultation: 'Vállalkozási és adózási forma konzultáció',
};

const SERVICE_LABEL_HU = {
  accounting: 'Könyvelés',
  payroll: 'Bérszámfejtés',
  consultation: 'Vállalkozási és adózási forma konzultáció',
  one_time_remainder: 'Egyéb egyszeri szolgáltatás',
  vaf: 'VAF konzultáció',
  ev_founding: 'EV alapítás',
};

const formatFt = (n) => {
  if (n == null || Number.isNaN(n)) return '—';
  return `${new Intl.NumberFormat('hu-HU').format(n)} Ft`;
};

const getTigPrimaryProviderCompanyName = (tig) =>
  String(tig?.providerName || '').trim() || '—';

const getTigSecondaryProviderCompanyName = (tig) =>
  String(tig?.secondaryProviderInfo?.name || '').trim() || '—';

const getTigProviderEntitySectionLabel = (tig, providerEntity) =>
  providerEntity === 'secondary'
    ? getTigSecondaryProviderCompanyName(tig)
    : getTigPrimaryProviderCompanyName(tig);

const hasSplitProviderEntitySections = (entityGroups) =>
  (entityGroups || []).some((g) => g.providerEntity === 'secondary');

const payrollLineKey = (line) => {
  const id = String(line.aspRequestId || '').trim();
  const customer = String(line.customerName || '').trim();
  return `${id || customer || 'payroll'}:payroll`;
};

const parseHeadcountInput = (raw) => {
  const n = parseInt(String(raw ?? '').replace(/\D/g, ''), 10);
  return Number.isFinite(n) && n > 0 ? n : 0;
};

const getPayrollUnitNet = (line) => {
  if (line.unitNetFee != null && !Number.isNaN(Number(line.unitNetFee))) {
    return Number(line.unitNetFee);
  }
  const hc = parseHeadcountInput(line.headcount || line.providerConfirmedHeadcount);
  if (hc > 0 && line.netFee != null) return Number(line.netFee) / hc;
  return Number(line.netFee) || 0;
};

const lineNetTotal = (line, headcountEdits) => {
  if (line.serviceKey !== 'payroll') return Number(line.netFee) || 0;
  const unit = getPayrollUnitNet(line);
  const hc = parseHeadcountInput(
    headcountEdits[payrollLineKey(line)] ?? line.providerConfirmedHeadcount ?? line.headcount,
  );
  return hc > 0 ? unit * hc : 0;
};

const sumGroupSubtotal = (group, headcountEdits) => {
  let sum = 0;
  for (const cat of group.categories || []) {
    for (const line of cat.lines || []) {
      sum += lineNetTotal(line, headcountEdits);
    }
  }
  return sum;
};

const BK_HEADCOUNT_INPUT_SX = {
  width: 88,
  '& .MuiOutlinedInput-root': {
    borderRadius: '8px',
    fontFamily: 'Satoshi, sans-serif',
    fontSize: '14px',
    backgroundColor: '#fff',
    '& fieldset': { borderColor: 'rgba(28, 50, 243, 0.35)' },
    '&:hover fieldset': { borderColor: '#1c32f3' },
    '&.Mui-focused fieldset': { borderColor: '#1c32f3', borderWidth: '2px' },
  },
  '& .MuiOutlinedInput-input': { py: 1, textAlign: 'center' },
};

const buildHeadcountEditsFromTig = (data) => {
  const edits = {};
  for (const group of data?.entityGroups || []) {
    for (const cat of group.categories || []) {
      for (const line of cat.lines || []) {
        if (line.serviceKey === 'payroll') {
          edits[payrollLineKey(line)] = String(
            line.providerConfirmedHeadcount ?? line.headcount ?? '',
          );
        }
      }
    }
  }
  return edits;
};

const collectPayrollHeadcountsPayload = (tig, headcountEdits) => {
  const rows = [];
  for (const group of tig?.entityGroups || []) {
    for (const cat of group.categories || []) {
      for (const line of cat.lines || []) {
        if (line.serviceKey !== 'payroll') continue;
        const hc = parseHeadcountInput(headcountEdits[payrollLineKey(line)]);
        if (!hc) return { error: `Adja meg a létszámot: ${line.customerName || 'bérszámfejtés'}` };
        rows.push({
          aspRequestId: line.aspRequestId,
          customerName: line.customerName,
          headcount: hc,
        });
      }
    }
  }
  return { payrollHeadcounts: rows };
};

export default function ProviderCsr() {
  const { uniqueId } = useParams();
  const [tig, setTig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [disputeDialogOpen, setDisputeDialogOpen] = useState(false);
  const [disputeComment, setDisputeComment] = useState('');
  const [disputeError, setDisputeError] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [headcountEdits, setHeadcountEdits] = useState({});

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await axios.get(
        `${API_BASE}/api/provider-tig/public/${encodeURIComponent(uniqueId)}`,
      );
      setTig(res.data);
      setHeadcountEdits(buildHeadcountEditsFromTig(res.data));
    } catch (e) {
      setError(e.response?.data?.error || 'A teljesítési igazolás nem található.');
      setTig(null);
      setHeadcountEdits({});
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [uniqueId]);

  const accept = async () => {
    const payload = collectPayrollHeadcountsPayload(tig, headcountEdits);
    if (payload.error) {
      alert(payload.error);
      return;
    }
    setActionLoading(true);
    try {
      const res = await axios.post(
        `${API_BASE}/api/provider-tig/public/${encodeURIComponent(uniqueId)}/accept`,
        payload,
        { headers: { 'Content-Type': 'application/json' } },
      );
      setTig(res.data);
      setHeadcountEdits(buildHeadcountEditsFromTig(res.data));
    } catch (e) {
      alert(e.response?.data?.error || 'Elfogadás sikertelen');
    } finally {
      setActionLoading(false);
    }
  };

  const openDisputeDialog = () => {
    setDisputeComment('');
    setDisputeError('');
    setDisputeDialogOpen(true);
  };

  const submitDispute = async () => {
    const msg = disputeComment.trim();
    if (!msg) {
      setDisputeError('Kérjük, írja le a módosítási igényét.');
      return;
    }
    setActionLoading(true);
    setDisputeError('');
    try {
      const res = await axios.post(
        `${API_BASE}/api/provider-tig/public/${encodeURIComponent(uniqueId)}/dispute`,
        { disputeComment: msg },
      );
      setTig(res.data);
      setDisputeDialogOpen(false);
    } catch (e) {
      setDisputeError(e.response?.data?.error || 'Módosítási igény elküldése sikertelen');
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <Container sx={{ py: 6, textAlign: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  if (error || !tig) {
    return (
      <Container sx={{ py: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  const status = tig.status || '';
  const statusHu = tig.statusLabelHu || STATUS_LABEL_HU[status] || status;
  const canRespond = tig.canProviderRespond || status === 'pending';

  const displayGrandTotal = (tig.entityGroups || []).reduce(
    (sum, group) => sum + sumGroupSubtotal(group, headcountEdits),
    0,
  );
  const entityGroups = tig.entityGroups || [];
  const primaryGroups = entityGroups.filter((g) => (g.providerEntity || 'primary') === 'primary');
  const secondaryGroups = entityGroups.filter((g) => g.providerEntity === 'secondary');
  const splitSections = hasSplitProviderEntitySections(entityGroups);

  const renderEntityGroup = (group) => {
    const groupSubtotal = sumGroupSubtotal(group, headcountEdits);
    const groupKey = `${group.providerEntity || 'primary'}::${group.entityName}`;
    return (
      <Box key={groupKey} sx={{ mb: 3 }}>
        <Typography variant="h6" sx={{ color: '#111E92' }}>
          {group.entityName} — {formatFt(groupSubtotal)}{' '}
          <Typography component="span" variant="caption" color="text.secondary">
            (nettó)
          </Typography>
        </Typography>
        {(group.categories || []).map((cat) => (
          <Box key={cat.categoryKey} sx={{ mt: 1 }}>
            <Typography variant="subtitle2" sx={{ color: '#48484a', mb: 0.5 }}>
              {CATEGORY_LABEL_HU[cat.categoryKey] || cat.categoryKey}
            </Typography>
            <Table
              size="small"
              sx={{
                '& th': {
                  fontWeight: 600,
                  color: '#111E92',
                  borderBottom: '2px solid rgba(28, 50, 243, 0.2)',
                },
                '& td': { borderColor: 'rgba(28, 50, 243, 0.08)' },
              }}
            >
              <TableHead>
                <TableRow>
                  <TableCell>Ügyfél</TableCell>
                  <TableCell>Szolgáltatás</TableCell>
                  <TableCell align="right">Nettó díj</TableCell>
                  <TableCell align="center" sx={{ width: 100 }}>
                    Létszám
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(cat.lines || []).map((line, idx) => {
                  const isPayroll = line.serviceKey === 'payroll';
                  const pKey = payrollLineKey(line);
                  const displayHc =
                    line.providerConfirmedHeadcount ??
                    (canRespond && isPayroll ? headcountEdits[pKey] : line.headcount);
                  return (
                    <TableRow key={`${line.aspRequestId}-${line.serviceKey}-${idx}`}>
                      <TableCell>{line.customerName}</TableCell>
                      <TableCell>{SERVICE_LABEL_HU[line.serviceKey] || line.serviceKey}</TableCell>
                      <TableCell align="right">
                        {isPayroll ? (
                          <Box>
                            <span>{formatFt(getPayrollUnitNet(line))} / fő (nettó)</span>
                            <Typography variant="caption" display="block" color="text.secondary">
                              Sor nettó:{' '}
                              {formatFt(
                                canRespond ? lineNetTotal(line, headcountEdits) : line.netFee,
                              )}
                            </Typography>
                          </Box>
                        ) : (
                          formatFt(canRespond ? lineNetTotal(line, headcountEdits) : line.netFee)
                        )}
                      </TableCell>
                      <TableCell align="center">
                        {isPayroll ? (
                          canRespond ? (
                            <TextField
                              size="small"
                              type="number"
                              inputProps={{ min: 1, max: 9999, step: 1 }}
                              value={headcountEdits[pKey] ?? ''}
                              onChange={(e) =>
                                setHeadcountEdits((prev) => ({
                                  ...prev,
                                  [pKey]: e.target.value,
                                }))
                              }
                              sx={BK_HEADCOUNT_INPUT_SX}
                            />
                          ) : (
                            <Typography variant="body2">{displayHc || '—'}</Typography>
                          )
                        ) : (
                          '—'
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Box>
        ))}
      </Box>
    );
  };

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Typography variant="h4" gutterBottom>
        Teljesítési igazolás
      </Typography>
      <Typography variant="subtitle1" color="text.secondary" gutterBottom>
        Könyvelői nézet — {tig.providerName}
      </Typography>

      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }} flexWrap="wrap">
        <Chip
          label={statusHu}
          color={status === 'accepted' ? 'success' : status === 'disputed' ? 'error' : 'default'}
        />
        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
          {tig.tigPublicId}
        </Typography>
        <Typography variant="body2">Elszámolási hónap: {tig.billingPeriod}</Typography>
      </Stack>

      {status !== 'pending' && (
        <Alert severity={status === 'accepted' ? 'success' : 'warning'} sx={{ mb: 2 }}>
          {status === 'accepted'
            ? 'Elfogadva — a számlázáshoz használd az alábbi TIG azonosítót a számla tétel megnevezésében.'
            : status === 'disputed'
              ? `Vitatott${tig.disputeComment ? `: ${tig.disputeComment}` : ''}`
              : `Státusz: ${statusHu}`}
        </Alert>
      )}

      {primaryGroups.length > 0 && (
        <>
          {splitSections && (
            <Typography variant="subtitle1" sx={{ mb: 2, mt: 1, color: '#111E92', fontWeight: 600 }}>
              {getTigProviderEntitySectionLabel(tig, 'primary')}
            </Typography>
          )}
          {primaryGroups.map((group) => renderEntityGroup(group))}
        </>
      )}

      {secondaryGroups.length > 0 && (
        <>
          <Typography variant="subtitle1" sx={{ mb: 2, mt: 3, color: '#111E92', fontWeight: 600 }}>
            {getTigProviderEntitySectionLabel(tig, 'secondary')}
          </Typography>
          {secondaryGroups.map((group) => renderEntityGroup(group))}
        </>
      )}

      <Typography variant="h6" align="right" sx={{ mb: 2, color: '#111E92' }}>
        Összesen (nettó): {formatFt(canRespond ? displayGrandTotal : tig.grandTotalNet)}
      </Typography>
      <Typography variant="body2" align="right" color="text.secondary" sx={{ mb: 2 }}>
        Az összegek nettóban, a szerződésben rögzített díjak alapján.
      </Typography>

      {(tig.underCollectionSection?.rows || []).length > 0 && (
        <Box sx={{ p: 2, bgcolor: '#f5f5f5', borderRadius: 1, mb: 2 }}>
          <Typography variant="subtitle1">Behajtás alatt</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Nem számlázható — tájékoztató jellegű.
          </Typography>
          {(tig.underCollectionSection.rows || []).map((row) => (
            <Typography key={row.aspRequestId} variant="body2">
              {row.customerName} ({row.bookkeepieEntityName})
            </Typography>
          ))}
        </Box>
      )}

      {status === 'accepted' && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Számlázáskor minden tétel megnevezésébe tedd be a TIG azonosítót, pl.{' '}
          <strong>Könyvelés {tig.tigPublicId}</strong>
        </Alert>
      )}

      {canRespond && (
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center" sx={{ mt: 3 }}>
          <Button
            variant="contained"
            disableElevation
            disabled={actionLoading}
            onClick={accept}
            sx={BK_PRIMARY_BUTTON_SX}
          >
            TIG elfogadása
          </Button>
          <Button
            variant="contained"
            disableElevation
            disabled={actionLoading}
            onClick={openDisputeDialog}
            sx={BK_TRANSPARENT_BUTTON_SX}
          >
            Módosítási igény
          </Button>
        </Stack>
      )}

      <Dialog open={disputeDialogOpen} onClose={() => !actionLoading && setDisputeDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Módosítási igény</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Írja le, mit szeretne módosítani a {tig.billingPeriod} havi teljesítési igazoláson. Üzenetét továbbítjuk a
            Bookkeepie csapatnak.
          </Typography>
          <TextField
            label="Módosítási igény / üzenet"
            multiline
            minRows={4}
            fullWidth
            required
            value={disputeComment}
            onChange={(e) => {
              setDisputeComment(e.target.value);
              if (disputeError) setDisputeError('');
            }}
            error={Boolean(disputeError)}
            helperText={disputeError}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            variant="contained"
            disableElevation
            onClick={() => setDisputeDialogOpen(false)}
            disabled={actionLoading}
            sx={BK_TRANSPARENT_BUTTON_SX}
          >
            Mégse
          </Button>
          <Button
            variant="contained"
            disableElevation
            onClick={submitDispute}
            disabled={actionLoading}
            sx={BK_PRIMARY_BUTTON_SX}
          >
            Módosítási igény elküldése
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
